<template>
  <Flex
    direction="column"
    fullWidth
  >
    <Flex
      :direction="$screen === 's' ? 'column' : 'row'"
      align="start"
      justify="between"
    >
      <Flex
        direction="column"
        :gap=".25"
      >
        <BaseHeading size="l">
          {{ $t('register.salonized_pay.dashboard.wallets.heading') }}
        </BaseHeading>
        <BaseText color="secondary">
          {{ $t('register.salonized_pay.dashboard.wallets.subheading', brandNamePay()) }}
          <BaseText
            inline
            :href="helpCenterArticleUrl(WALLETS_ARTICLE_ID)"
            v-test="'sp-wallets-help'"
          >
            {{ $t('global.learn_more') }}
          </BaseText>
        </BaseText>
      </Flex>
      <Flex
        v-if="canManageWallets"
        :gap="0.5"
      >
        <BaseButton
          color="inverted"
          @click="showBankAccountsModal = true"
          v-test="'sp-wallets-manage-bank-accounts'"
        >
          {{ $t('register.salonized_pay.dashboard.wallets.bank_accounts') }}
        </BaseButton>
        <BaseButton
          icon="plus"
          :disabled="!canAddWallet"
          @click="showAddWalletModal = true"
          v-test="'sp-wallets-add-button'"
        >
          {{ $t('register.salonized_pay.dashboard.wallets.add') }}
        </BaseButton>
      </Flex>
    </Flex>

    <div
      v-if="loading && !balanceAccounts.length"
      :class="$style.loading"
      v-test="'sp-wallets-loading'"
    >
      <BaseSpinner inline />
    </div>
    <WalletCard
      v-for="balanceAccount in balanceAccounts"
      :key="balanceAccount.id"
      :balanceAccount="balanceAccount"
      :canEdit="canManageWallets"
      @edit="editingBalanceAccount = balanceAccount"
      @remove="removingBalanceAccount = balanceAccount"
      @viewPayouts="$emit('viewPayouts', balanceAccount.id)"
      v-test="'sp-wallet'"
    />
    <WalletReport />

    <BankAccountsModal
      v-if="showBankAccountsModal"
      @close="showBankAccountsModal = false"
    />
    <RemoveWalletModal
      v-if="removingBalanceAccount"
      :balanceAccount="removingBalanceAccount"
      @close="removingBalanceAccount = null"
    />
    <WalletModal
      v-if="showAddWalletModal || editingBalanceAccount"
      :balanceAccount="editingBalanceAccount ?? undefined"
      :balanceAccounts="balanceAccounts"
      @close="closeWalletModal"
      @generateOnboardingLink="$emit('generateOnboardingLink')"
    />
  </Flex>
</template>

<script
  lang="ts"
  setup
>
import { useQuery } from '@vue/apollo-composable';
import { computed, ref } from 'vue';
import { brandNamePay } from '@/helpers/brand-name';
import { helpCenterArticleUrl } from '@/helpers/help-center';
import { useUserStore } from '@/stores/user';
import { type AdyenBalanceAccount, AdyenBalanceAccountStatus } from '@/types';
import { GET_ADYEN_WALLETS } from '../graphql';
import BankAccountsModal from './BankAccountsModal.vue';
import RemoveWalletModal from './RemoveWalletModal.vue';
import WalletCard from './WalletCard.vue';
import WalletModal from './WalletModal.vue';
import WalletReport from './WalletReport.vue';

// Matches the backend's per-salon cap in Adyen::LabelledBalanceAccountCreator.
const MAX_WALLETS = 10;
const WALLETS_ARTICLE_ID = 17602926;

defineEmits<{
  generateOnboardingLink: [];
  viewPayouts: [balanceAccountId: number];
}>();

const { hasFeatureFlag } = useUserStore();

const { result, loading } = useQuery(GET_ADYEN_WALLETS);

// Closed wallets stay in the response but are gone for the salon.
const balanceAccounts = computed<AdyenBalanceAccount[]>(() =>
  (result.value?.adyenAppState?.balanceAccounts ?? []).filter(
    (balanceAccount: AdyenBalanceAccount) =>
      balanceAccount.status !== AdyenBalanceAccountStatus.Closed,
  ),
);

const showBankAccountsModal = ref(false);
const showAddWalletModal = ref(false);
const editingBalanceAccount = ref<AdyenBalanceAccount | null>(null);
const removingBalanceAccount = ref<AdyenBalanceAccount | null>(null);

const closeWalletModal = () => {
  showAddWalletModal.value = false;
  editingBalanceAccount.value = null;
};

// Creating and editing wallets is owner-only; the backend checks the same flag.
const canManageWallets = computed(() => hasFeatureFlag('admin-salonized-pay'));

const canAddWallet = computed(
  () =>
    result.value?.adyenAppState?.verificationStatus === 'VALID' &&
    balanceAccounts.value.length < MAX_WALLETS,
);
</script>

<style
  lang="scss"
  module
>
.loading {
  padding: $spacing * 2 0;
}
</style>
